/**
 * useTenantCopy — the app's single read path for tenant-specific COPY
 * (demo-tenant-environment Task 23, Req 2.2, 2.3).
 *
 * Sits beside `useBranding()` and reads the same two signals `BrandingContext`
 * already resolves — the verified active-tenant claim and the resolved brand
 * name — then hands back a fully-resolved `TenantCopy`. A component asks for the
 * copy it needs; it never holds the copy itself.
 *
 * TOLERANT OF A MISSING PROVIDER, ON PURPOSE. `useBranding()` and `useAuth()`
 * both throw outside their providers, which is correct for brand chrome and for
 * auth actions but wrong here: plenty of surfaces (and every component test that
 * renders a leaf in isolation) mount without them, and Req 2.3 already says what
 * an unresolvable tenant should produce — the NEUTRAL placeholder. So these hooks
 * read the branding context optionally and take the tenant claim through
 * `useActiveTenantId()`, the provider-tolerant reader tenant-foundation added for
 * exactly this reason. That makes "no provider" and "provider, read failed" the
 * same answer, which is the only answer that cannot leak.
 */

import { useContext, useMemo } from 'react';

import { useActiveTenantId } from '@/context/AuthContext';
import BrandingContext from '@/context/BrandingContext';
import { NEUTRAL_BRAND_NAME } from '@/lib/tenantBranding';
import { resolveTenantCopy, type TenantCopy } from '@/lib/tenantCopy';

/**
 * The active tenant's resolved brand name — `identity.name` from
 * `BrandingContext`, or the neutral name when no provider is mounted.
 *
 * Read this (never a literal) wherever the tenant's own name is displayed as
 * copy rather than as brand chrome.
 */
export function useTenantBrandName(): string {
  const branding = useContext(BrandingContext);
  const name = branding?.identity.name?.trim();
  return name && name.length > 0 ? name : NEUTRAL_BRAND_NAME;
}

/**
 * The active tenant's copy, resolved from configuration keyed to the active
 * tenant, with the neutral tenant-agnostic placeholder as the only fallback.
 */
export function useTenantCopy(): TenantCopy {
  const branding = useContext(BrandingContext);
  const activeTenantId = useActiveTenantId();
  const brandName = branding?.identity.name;

  return useMemo(
    () => resolveTenantCopy({ activeTenantId, brandName }),
    [activeTenantId, brandName],
  );
}
